

















































































































































































































































































































































import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import OzIcon from 'library/v4/components/OzIcon.vue'

export enum OzButtonColorScheme {
  primary = 'primary',
  secondary = 'secondary',
  active = 'active',
  pulsingPrimary = 'pulsingPrimary',
  danger = 'danger',
  alternative = 'alternative',
  orange = 'orange',
}

/**
 * The true button
 */
@Component({ components: { OzIcon } })
export default class OzButton extends Vue {
  $el!: HTMLDivElement | HTMLButtonElement
  $refs!: {
    text: HTMLDivElement
  }

  /**
   * If dir="rtl" is included, icons are mirrored approriately
   * @default ltr
   */
  @Prop({ default: 'ltr' }) dir!: string

  /**
   * Determines if button is disabled
   * @default false
   */
  @Prop({ default: false }) disabled!: boolean

  /**
   * Determines the color of button. Use `active` when you want to force the selection color
   * @values primary, secondary, active, pulsingPrimary, danger, alternative, orange
   * @default primary
   */
  @Prop({ default: 'primary' }) colorScheme!: OzButtonColorScheme

  /**
   * Determines the type of button.
   * Filled button is the default with colored background.
   * Text button is without background and colored text.
   * Custom button will not have any style
   * @values filled, text, custom
   * @default filled
   */
  @Prop({ default: 'filled' }) type!: string

  /**
   * Font size for text
   * @values 12, 14, 17, 24
   * 12 is for the post moderation component, 14 is used in the content picker and post comment, 17 is the default used in most cases, 24 is used for grading reaction input numpad
   * @default 17
   */
  @Prop({ default: 17, type: Number }) fontSize!: number

  /**
   * Font weight for text
   * @values default, light
   * @default default
   */
  @Prop({ default: 'default' }) fontWeight?: 'light' | 'default' | 'bold'

  /**
   * Determines if there should be padding for text
   * @default true
   */
  @Prop({ default: true }) xTextPadding!: boolean

  /**
   * Allows clicking the button navigate to a URL
   */
  @Prop({ default: undefined }) href?: string

  /**
   * Name of OzIcon to load
   */
  @Prop({ default: undefined }) iconName?: string

  /**
   * Name of OzIcon to load
   * @default oricon
   * @values immaterial, oricon
   */
  @Prop({ default: 'oricon' }) iconFont?: string

  /**
   * Size of OzIcon to load
   */
  @Prop({ default: 24, type: Number }) iconSize!: number

  /**
   * Determines if the button should use the fixed 40px width
   * @default true
   */
  @Prop({ default: true }) shouldFixWidth!: boolean

  /**
   * Determines if the button should use the fixed 40px height
   * @default true
   */
  @Prop({ default: true }) shouldFixHeight!: boolean

  /**
   * Size of the button
   * @values default, small
   */
  @Prop({ default: 'default' }) size!: 'default' | 'small'

  /**
   * Determines if the button's background color should be less prominent compared to usual (currently only for secondary color scheme)
   * @default false
   */
  @Prop({ default: false }) xLessProminentBackground!: boolean

  /**
   * Dark type
   */
  @Prop({ default: 'browser' }) darkThemeType!: 'browser' | 'custom'

  /**
   * If darkThemeType is custom, toggle dark or light color mode.
   */
  @Prop({ default: false }) isDark!: boolean

  /**
   * Determines if button's border radius will be decided in the use site
   * @default false
   */
  @Prop({ default: false }) isCustomBorderRadius!: boolean

  /**
   * Get text element
   * @public
   */
  public get textElement(): HTMLDivElement {
    return this.$refs.text
  }

  get isBrowserDecidingDarkMode(): boolean {
    return this.darkThemeType === 'browser'
  }

  get isForcedLight(): boolean {
    return this.darkThemeType === 'custom' && !this.isDark
  }

  get isForcedDark(): boolean {
    return this.darkThemeType === 'custom' && this.isDark
  }

  get hasText(): boolean {
    return !!this.$slots.default && this.$slots.default.toString().trim() !== ''
  }

  get hasIcon(): boolean {
    return !!this.iconName
  }

  get hasIconOnly(): boolean {
    return this.hasIcon && !this.hasText
  }

  get hasIconAndText(): boolean {
    return this.hasIcon && this.hasText
  }

  get isColorPrimary(): boolean {
    return this.colorScheme === OzButtonColorScheme.primary
  }

  get isColorSecondary(): boolean {
    return this.colorScheme === OzButtonColorScheme.secondary
  }

  get isColorDanger(): boolean {
    return this.colorScheme === OzButtonColorScheme.danger
  }

  get isColorActive(): boolean {
    return this.colorScheme === OzButtonColorScheme.active
  }

  get isColorPulsingPrimary(): boolean {
    return this.colorScheme === OzButtonColorScheme.pulsingPrimary
  }

  get isColorOrange(): boolean {
    return this.colorScheme === OzButtonColorScheme.orange
  }

  get isColorAlternative(): boolean {
    return this.colorScheme === OzButtonColorScheme.alternative
  }

  get isTextButton(): boolean {
    return this.type === 'text'
  }

  get isCustomButton(): boolean {
    return this.type === 'custom'
  }

  get tagName(): string {
    return this.href ? 'a' : 'button'
  }

  get role(): string | boolean {
    return this.href ? 'button' : false
  }

  get fontWeightClass(): string {
    switch (this.fontWeight) {
      case 'light':
        return 'font-medium'
      case 'default':
        return 'font-semibold'
      case 'bold':
        return 'font-bold'
      default:
        return 'font-semibold'
    }
  }

  get fontSizeClass(): string {
    switch (this.fontSize) {
      case 12:
        return 'text-12'
      case 14:
        return 'text-14'
      case 17:
        return 'text-17'
      case 24:
        return 'text-heading-2'
      default:
        return 'text-17'
    }
  }

  get heightSizeClass(): string {
    switch (this.size) {
      case 'small':
        return 'h-8'
      case 'default':
      default:
        return 'h-10'
    }
  }

  get widthSizeClass(): string {
    switch (this.size) {
      case 'small':
        return 'w-8'
      case 'default':
      default:
        return 'w-10'
    }
  }

  get borderRadiusClass(): string {
    switch (this.size) {
      case 'small':
        return 'rounded-xl'
      case 'default':
      default:
        return 'rounded-2xl'
    }
  }

  click(event: MouseEvent): void {
    /**
     * Click event
     * @property {MouseEvent} event click event
     */
    this.$emit('click', event)
  }

  handleSpaceKey(event: KeyboardEvent): void {
    // Anchor elements with roles set to ‘button’ can only be activated with the ‘enter’ key,
    // whereas a button element would be triggered by both ‘space’ and ‘enter’ keys.
    // Adding a space keypress event listener and calling this method should ensure
    // that the element triggers the same event that a click, or pressing the ‘enter’ key
    // would (mouse click event) and execute the default behavior.
    const SPACE_KEYCODE = event.code === 'Space'
    if (this.href && SPACE_KEYCODE) {
      event.preventDefault()
      // prevent default scrolling behaviour of anchor tags
      const linkButton = this.$el
      linkButton.click()
    }
  }
}
